import BumbleTabs from '@bumble/app/components/BumbleTabs';
import CreateModal from '@bumble/app/components/CreateModal';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { Indicator } from '@bumble/app/components/Indicator';
import { Loading } from '@bumble/app/components/Loading';
import MultipleSelections from '@bumble/app/components/MultipleSelections';
import NodeMetrics from '@bumble/app/components/NodeMetrics';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { PageLoading } from '@bumble/app/components/PageLoading';
import {
  RouterAssignEntity,
  RouterCpeInfoEdit,
  RouterCreate,
  RouterDownloadResult,
  RouterHandleService,
  RouterNetworkInfoEdit,
  RouterNetflowEdit,
  RouterIperfServerEdit,
  RouterPeerTest,
  RouterSiteEdit,
  RouterTabDetails,
  RouterGetDiagnostic,
  RouterResetLANPort,
  RouterPingAndTraceroute,
  RouterDailyPerformanceTest,
  RouterGeoVisibilityEdit,
  RouterDNIEdit,
  RouterIPMonitorTargetEdit,
  RouterDDosMitigationEdit,
  RouterBlockMaliciousEdit,
} from '@bumble/app/components/router';
import SplitPanes from '@bumble/app/components/SplitPanes';
import { useApi } from '@bumble/app/hooks/use-api';
import { CpeInterfaces } from '@bumble/app/types/cpeTypes';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditIcon from '@mui/icons-material/Edit';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SpeedIcon from '@mui/icons-material/Speed';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { Box, Card, CardContent } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAccount } from '../hooks/use-account';
import { apiError } from '../utils/api-error';
import Select from 'react-select';

export const Routers = () => {
  const api = useApi();
  const { account } = useAccount();

  const [entities, setEntities] = useState<any[]>([{label: 'None', value: 'none'}]);
  const [entity, setEntity] = useState<any>(entities[0]);

  const { isLoading: isLoading2, data: entityNames } = useQuery(
    ['prepareEntityNames'],
    api.getEntityNamesApiV1GetEntityNamesGet.bind(null, {}),
    { enabled: true, cacheTime: 0, initialData: undefined },
  );

  useEffect(() => {
    const entity_data = entityNames?.data;
    let actualEntities = [{label: 'None', value: 'none'}];
    if (entity_data && entity_data.length) {
      actualEntities = actualEntities.concat(entity_data.map((e: string) => ({
        label: e,
        value: e,
      })))
    }
    setEntities(actualEntities);
    setEntity(actualEntities[0]);
  }, [entityNames]);

  const changeEntity = (val: any) => {
    setEntity(val);
  };

  const { isLoading, data, refetch } = useQuery(
    ['routers', entity],
    api.getCpeApiV1GetCpeGet.bind(
      null,
      entity?.value && entity.value !== 'none' ? { entity_name: entity.value } : {}
    ),
    { enabled: true },
  );

  const columns: GridColDef[] = [
    {
      field: 'cpe_name',
      headerName: 'Name',
      //flex: 1,
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'entity_name',
      headerName: 'Entity Name',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'site_address',
      headerName: 'Site Address',
      flex: 1,
      minWidth: 300,
      disableColumnMenu: true,
    },
    {
      field: 'prod_code',
      headerName: 'Product Code',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'admin_state',
      headerName: 'Admin State',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'op_state',
      headerName: 'Op State',
      width: 150,
      disableColumnMenu: true,
      renderCell: (params) => (params.value !== undefined ? <Indicator active={params.value === 'up'} /> : 'N/A')
    },
    {
      field: 'provider_service_id',
      headerName: 'Provider Service ID',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'client_circuit_id',
      headerName: 'Client Circuit ID',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'account_name',
      headerName: 'Account Name',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'cpe_id',
      headerName: 'Device ID',
      //flex: 1,
      width: 200,
      disableColumnMenu: true,
    },
    //{ field: 'site_address' },
    { field: 'shipping_address' },
    { field: 'state' },
    { field: 'test_status' },
    { field: 'description' },
  ];

  const columnVisible = {
    cpe_name: true,
    cpe_id: true,
    prod_code: true,
    admin_state: true,
    op_state: true,
    provider_service_id: true,
    client_circuit_id: true,
    entity_id: true,
    account_name: account?.master_account,
    //entity_name: account?.master_account,
    entity_name: true,
    site_address: true,
    shipping_address: false,
    state: false,
    test_status: false,
    description: false,
  };

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [showEditCPEInfoDialog, setShowEditCPEInfoDialog] = useState<boolean>(false);
  const [showEditNetworkInfoDialog, setShowEditNetworkInfoDialog] = useState<boolean>(false);
  const [showEditIPMonitorDialog, setShowEditIPMonitorDialog] = useState<boolean>(false);
  const [showEditDNIDialog, setShowEditDNIDialog] = useState<boolean>(false);
  const [showEditNetflowDialog, setShowEditNetflowDialog] = useState<boolean>(false);
  const [showEditIperfDialog, setShowEditIperfDialog] = useState<boolean>(false);
  const [showEditGeoDialog, setShowEditGeoDialog] = useState<boolean>(false);
  const [showEditBlockMalicious, setShowEditBlockMalicious] = useState<boolean>(false);
  const [showEditDDosMitigation, setShowEditDDosMitigation] = useState<boolean>(false);
  const [showPeerTestDialog, setShowPeerTestDialog] = useState<boolean>(false);
  const [showDailyPerformanceTest, setShowDailyPerformanceTest] = useState<boolean>(false);
  const [showRetrieveResultDialog, setShowRetrieveResultDialog] = useState<boolean>(false);
  const [showAssignEntity, setShowAssignEntity] = useState<boolean>(false);
  const [showEditSiteDialog, setShowEditSiteDialog] = useState<boolean>(false);
  const [showGetDiagnosticDialog, setShowGetDiagnosticDialog] = useState<boolean>(false);
  const [showResetLANPortsDialog, setShowResetLANPortsDialog] = useState<boolean>(false);
  const [showResetDeviceDialog, setShowResetDeviceDialog] = useState<boolean>(false);
  const [showPingDialog, setShowPingDialog] = useState<boolean>(false);
  const [mode, setMode] = useState<{ value: number, flag?: boolean }>({ value: 0 });
  const [showHanldeServiceDialog, setShowHandleServiceDialog] = useState<{
    status: boolean, value: boolean
  }>({ status: true, value: false });
  const [menuItems, setMenuItems] = useState<{
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    hasDivider?: boolean;
    disabled?: boolean;
  }[]>([]);

  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);

  const removeRouter = useMutation(api.removeCpeApiV1RemoveCpePost, {
    onSuccess() {
      toast.success('CPE router removed.');
      setLoadingModalOpen(false);
      handleDataRefetch();
    },
    onError(error: any) {
      apiError(error);
      setLoadingModalOpen(false);
    },
  });

  const resetNode = useMutation(api.resetNodeApiV1ResetNodePost, {
    onSuccess() {
      toast.success('CPE router reseted successully.');
      setLoadingModalOpen(false);
      handleDataRefetch();
    },
    onError(error: any) {
      apiError(error);
      setLoadingModalOpen(false);
    },
  })

  useEffect(() => {
    let items = [];
    switch (mode.value) {
      case 1:
        items.push(...[{
          label: 'Edit Site',
          icon: <EditIcon color="info" fontSize="small" />,
          onClick: handleEditSite,
        }, {
          label: 'Activate Service',
          icon: <PlayArrowIcon color="info" fontSize="small" />,
          onClick: () => handleService(true),
          hasDivider: true,
          disabled: !mode?.flag
        }, {
          label: 'Stop Service',
          icon: <PauseIcon color="info" fontSize="small" />,
          onClick: () => handleService(false),
          hasDivider: false,
          disabled: mode?.flag
        }]);
        break;
      default:
        items.push(...[{
          label: 'Edit CPE Info',
          icon: <EditIcon color="info" fontSize="small" />,
          onClick: handleEditCPEInfo,
        }, {
          label: 'Edit Network Info',
          icon: null,
          onClick: handleEditNetworkInfo,
        }, {
          label: 'Assign Entity',
          icon: null,
          onClick: () => setShowAssignEntity(true),
        }, {
          label: 'Start Activation Test',
          icon: <SpeedIcon color="info" fontSize="small" />,
          onClick: handlePeerTest,
          hasDivider: true,
        }, {
          label: 'Retrieve Activation Test Report',
          icon: null,
          onClick: handleRetrieveResult,
        }, {
          label: 'Edit Daily Performance Test',
          icon: null,
          onClick: handleDailyPerformanceTest,
        }, {
          label: 'Edit Appflow',
          icon: <ToggleOnIcon color="info" fontSize="small" />,
          onClick: handleEditNetflow,
          hasDivider: true,
        }, {
          label: 'Edit Down Notification Interval',
          icon: null,
          onClick: handleEditDownNotificationInterval,
        }, {
          label: 'Edit IP Monitor Target',
          icon: null,
          onClick: handleEditIPMonitor,
        }, {
          label: 'Edit iPerf Server',
          icon: null,
          onClick: handleEditIperfServer,
        }, {
          label: 'Edit Geo Visibility',
          icon: <TravelExploreIcon color="info" fontSize="small" />,
          onClick: handleEditGeo,
          hasDivider: true,
        }, {
          label: 'Edit Block Malicious Sources',
          icon: null,
          onClick: handleBlockMalicious,
        }, {
          label: 'Edit DDoS Mitigation',
          icon: null,
          onClick: handleDDosMitigation,
        }]);
        break;
    }
    items.push(...[{
      label: 'Get Diagnostics',
      icon: <TroubleshootIcon color="info" fontSize="small" />,
      onClick: handleGetDiagnostic,
      hasDivider: true,
    }, {
      label: 'Reset LAN Ports',
      icon: null,
      onClick: handleResetLAN,
    }, {
      label: 'Reset Device',
      icon: null,
      onClick: handleResetDevice,
    }, {
      label: 'Ping and Traceroute',
      icon: null,
      onClick: handlePing,
    }, {
      label: 'Delete',
      icon: <DeleteForeverOutlinedIcon color="error" fontSize="small" />,
      onClick: () => setShowConfirmDelete(true),
      hasDivider: true,
    }]);
    setMenuItems([...items]);
  }, [mode]);

  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedItemId(selectedRowItem);
      data?.data.filter(item => item.cpe_id === selectedRowItem).map((item) => {
        switch (item.prod_code) {
          case 'BN-CPE-IP':
            setMode({ value: 1, flag: item.admin_state === 'active' ? false : true });
            break;
          default:
            setMode({ value: 0 });
        }
      })
    } else setSelectedItemId('');
  }, [rowSelectionModel, selectedRowItem]);

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection.slice(-1));
  };

  const handleDataRefetch = () => {
    refetch();
    toast.success('Refreshed internet NIDs.');
  };

  const handleCreateModalOpen = () => {
    setOpenCreateModal(true);
  };

  const handleCreateModalClose = () => {
    setOpenCreateModal(false);
    handleDataRefetch();
  };

  const handleRouterDelete = () => {
    setLoadingModalOpen(true);
    removeRouter.mutate({ cpe_id: selectedItemId });
  };

  const handleRouterResetDevice = () => {
    setLoadingModalOpen(true);
    resetNode.mutate({
      node_id: selectedItemId,
      hard: true,
    })
  }

  const handleEditCPEInfo = () => {
    setShowEditCPEInfoDialog(true);
  };

  const handleEditIPMonitor = () => {
    setShowEditIPMonitorDialog(true);
  };

  const handleEditDownNotificationInterval = () => {
    setShowEditDNIDialog(true);
  };

  const handleEditNetworkInfo = () => {
    setShowEditNetworkInfoDialog(true);
  };

  const handleEditNetflow = () => {
    setShowEditNetflowDialog(true);
  };

  const handleEditIperfServer = () => {
    setShowEditIperfDialog(true);
  }

  const handleEditGeo = () => {
    setShowEditGeoDialog(true);
  }

  const handleBlockMalicious = () => {
    setShowEditBlockMalicious(true);
  }

  const handleDDosMitigation = () => {
    setShowEditDDosMitigation(true);
  }

  const handlePeerTest = () => {
    setShowPeerTestDialog(true);
  }

  const handleDailyPerformanceTest = () => {
    setShowDailyPerformanceTest(true);
  }

  const handleRetrieveResult = () => {
    setShowRetrieveResultDialog(true);
  }

  const handleService = (status: boolean) => {
    setShowHandleServiceDialog({ status, value: true });
  }

  const handleEditSite = () => {
    setShowEditSiteDialog(true);
  }

  const handleGetDiagnostic = () => {
    setShowGetDiagnosticDialog(true);
  }

  const handleResetLAN = () => {
    setShowResetLANPortsDialog(true);
  }

  const handleResetDevice = () => {
    setShowResetDeviceDialog(true);
  }

  const handlePing = () => {
    setShowPingDialog(true);
  }

  const {
    // isLoading: detailLoading,
    data: detailData,
    refetch: detailRefetch,
  } = useQuery(
    ['routerDetail'],
    api.getCpeDetailApiV1GetCpeDetailGet.bind(null, {
      cpe_id: selectedRowItem,
    }),
    { enabled: false },
  );

  useEffect(() => {
    if (selectedRowItem) detailRefetch();
  }, [detailRefetch, selectedRowItem]);

  const handlePeerTestDialogClose = (cpeId: string, sse_id: string) => {
    setShowPeerTestDialog(false);
    if (sse_id) {
      startSSE(cpeId, sse_id);
    }
  };

  let eventSource: any;
  let retryTimeout = 1000;
  let maxTimeout = 30000;
  let reconnecting = false;

  const { storageToken } = useAccount();
  const session = storageToken();

  const startSSE = (cpe_id: string, sse_id: string) => {
    if (eventSource) eventSource.close();
    eventSource = new EventSource(`${import.meta.env.VITE_API}/check_cpe_test_status?cpe_id=${cpe_id}&sse_id=${sse_id}`);
    eventSource.onmessage = (event: any) => {
      toast.success(event.data, {autoClose: false});
      retryTimeout = 1000;
      stopSSE();
    },
    eventSource.onerror = (event: any) => {
      console.error('Connection lost.');
      if (eventSource.readyState === EventSource.CLOSED) {
        console.log('Server closed connection normally. Not reconnecting.');
        return;
      }
      console.log('Retrying in', retryTimeout / 1000, 'seconds...');
      eventSource.close();
      eventSource = null;
      if (!reconnecting) {
        reconnecting = true;
        setTimeout(() => {
          reconnecting = false;
          retryTimeout = Math.min(retryTimeout * 2, maxTimeout);
          startSSE(cpe_id, sse_id);
        }, retryTimeout);
      }
    };
  }

  const stopSSE = () => {
    if (eventSource) {
      eventSource.close();
      eventSource = null;
      console.log('SSE connection closed by user.');
    }
  }

  useEffect(() => {
    switch (detailData?.data.prod_code) {
      case 'BN-CPE-IP':
        setMode({
          value: 1,
          flag: detailData?.data.admin_state === 'active' ? false : true
        });
        break;
      default:
        setMode({ value: 0 });
    }
  }, [detailData])

  return (
    <PageContent>
      <PageHeader pageName="Internet NIDs" count={data?.data?.length || 0} />
      <Box sx={{ mb: 5, position: 'absolute', top: 140, left: 570 }}>
        <Select options={entities} defaultValue={entities[0]} value={entity} onChange={changeEntity} styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            borderRadius: '8px',
            width: '300px',
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            width: '300px',
          }),
        }}/>
      </Box>
      {isLoading ? <Loading /> : (<>
        <SplitPanes
          top={
            <Card elevation={0}>
              <CardContent>
                <DataTableGrid
                  rows={
                    data?.data.map((item) => {
                      return {
                        ...item,
                        id: item.cpe_id,
                      };
                    }) || []
                  }
                  columns={columns}
                  handleSelectionChange={handleSelectionChange}
                  rowSelectionModel={rowSelectionModel}
                  itemsSelected={rowSelectionModel.length === 1}
                  newLabel="Create Device"
                  newOnClick={handleCreateModalOpen}
                  columnVisibilityModel={columnVisible}
                  refreshData={handleDataRefetch}
                  menuItems={menuItems}
                  pagination={true}
                />
              </CardContent>
            </Card>
          }
          bottom={
            <Card elevation={0} sx={{ mt: 5 }}>
              <CardContent>
                {rowSelectionModel.length === 1 ? (
                  <BumbleTabs
                    tabs={mode.value == 1 ? [
                      {
                        el: <RouterTabDetails data={detailData?.data} mode={mode.value} />,
                        label: 'Details',
                        id: 0,
                      }
                    ] : [
                      {
                        el: <RouterTabDetails data={detailData?.data} />,
                        label: 'Details',
                        id: 0,
                      },
                      {
                        el: <NodeMetrics id={selectedRowItem} />,
                        label: 'Monitoring',
                        id: 1,
                      },
                    ]}
                  />
                ) : (
                  <MultipleSelections count={rowSelectionModel.length} />
                )}
              </CardContent>
            </Card>
          }
        />
      </>)}
      {openCreateModal && (
        <CreateModal
          open={openCreateModal}
          handleClose={handleCreateModalClose}
          title="Create Device"
        >
          <RouterCreate handleClose={handleCreateModalClose} />
        </CreateModal>
      )}
      {showEditCPEInfoDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditCPEInfoDialog}
          handleClose={() => setShowEditCPEInfoDialog(false)}
          title="Edit CPE info"
        >
          <RouterCpeInfoEdit
            onSubmit={() => {
              setShowEditCPEInfoDialog(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            cpeName={detailData?.data?.cpe_name}
            shippingAddress={detailData?.data?.shipping_address || ''}
            siteAddress={detailData?.data?.site_address || ''}
            providerServiceID={detailData?.data?.provider_service_id || ''}
            clientCircuitID={detailData?.data?.client_circuit_id || ''}
            localContactName={detailData?.data?.contact_name || ''}
            localContactEmail={detailData?.data?.contact_email || ''}
            description={detailData?.data?.description || ''}
            pingEmailNotification={detailData?.data?.ping_email_notification || ''}
            pingWebhookNotification={detailData?.data?.ping_webhook_notification || ''}
          />
        </DialogStandard>
      )}
      {showEditNetworkInfoDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditNetworkInfoDialog}
          handleClose={() => setShowEditNetworkInfoDialog(false)}
          title="Edit Network Info"
        >
          <RouterNetworkInfoEdit
            onSubmit={() => {
              setShowEditNetworkInfoDialog(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            prodCode={detailData.data.prod_code}
            mode={detailData.data.mode ? detailData.data.mode : 'ROUTER'}
            ports={detailData.data.ports as CpeInterfaces}
          />
        </DialogStandard>
      )}
      {showEditIPMonitorDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditIPMonitorDialog}
          handleClose={() => setShowEditIPMonitorDialog(false)}
          title="Edit IP Monitor Target"
        >
          <RouterIPMonitorTargetEdit
            onSubmit={() => {
              setShowEditIPMonitorDialog(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            ipMonitorTarget={detailData.data.ip_monitor_target || ''}
          />
        </DialogStandard>
      )}
      {showEditDNIDialog && selectedItemId && (
        <DialogStandard
          open={showEditDNIDialog}
          handleClose={() => setShowEditDNIDialog(false)}
          title="Edit Down Notification Interval"
        >
          <RouterDNIEdit
            onSubmit={() => {
              setShowEditDNIDialog(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            scanGap={detailData?.data.scan_gap || 1}
          />
        </DialogStandard>
      )}
      {showEditNetflowDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditNetflowDialog}
          handleClose={() => setShowEditNetflowDialog(false)}
          title="Edit Appflow"
        >
          <RouterNetflowEdit
            onSubmit={() => {
              setShowEditNetflowDialog(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            netflowEnabled={detailData.data.netflow_enabled || false}
          />
        </DialogStandard>
      )}
      {showEditIperfDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditIperfDialog}
          handleClose={() => setShowEditIperfDialog(false)}
          title="Edit iPerf Server"
        >
          <RouterIperfServerEdit
            onSubmit={() => {
              setShowEditIperfDialog(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            iPerfServerEnabled={detailData.data.iperf_server || false}
          />
        </DialogStandard>
      )}
      {showEditGeoDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditGeoDialog}
          handleClose={() => setShowEditGeoDialog(false)}
          title="Edit Geo Visibility"
        >
          <RouterGeoVisibilityEdit
            onSubmit={() => {
              setShowEditGeoDialog(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            geoEnabled={(detailData.data.monitor_inbound_pub_ip_enabled && detailData.data.monitor_outbound_pub_ip_enabled) || false}
          />
        </DialogStandard>
      )}
      {showEditBlockMalicious && selectedItemId && detailData && (
        <DialogStandard
          open={showEditBlockMalicious}
          handleClose={() => setShowEditBlockMalicious(false)}
          title="Edit Block Malicious Sources"
        >
          <RouterBlockMaliciousEdit
            onSubmit={() => {
              setShowEditBlockMalicious(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            isEnabled={detailData.data.malicious_ip_block_enabled || false}
          />
        </DialogStandard>
      )}
      {showEditDDosMitigation && selectedItemId && detailData && (
        <DialogStandard
          open={showEditDDosMitigation}
          handleClose={() => setShowEditDDosMitigation(false)}
          title="Edit DDoS Mitigation"
        >
          <RouterDDosMitigationEdit
            onSubmit={() => {
              setShowEditDDosMitigation(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            dDosEnabled={detailData.data.denial_of_service_enabled || false}
          />
        </DialogStandard>
      )}
      {showPeerTestDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showPeerTestDialog}
          handleClose={() => setShowPeerTestDialog(false)}
          title="Start Activation Test"
        >
          <RouterPeerTest
            cpeId={selectedItemId}
            close={handlePeerTestDialogClose}
          />
        </DialogStandard>
      )}
      {showRetrieveResultDialog && selectedItemId && (
        <DialogStandard
          size='md'
          open={showRetrieveResultDialog}
          handleClose={() => setShowRetrieveResultDialog(false)}
          title="Retrieve Activation Test Report"
        >
          <RouterDownloadResult cpeId={selectedItemId}/>
        </DialogStandard>
      )}
      {showDailyPerformanceTest && selectedItemId && detailData && (
        <DialogStandard
          open={showDailyPerformanceTest}
          handleClose={() => setShowDailyPerformanceTest(false)}
          title="Edit Daily Performance Test"
        >
          <RouterDailyPerformanceTest
            cpeId={selectedItemId}
            perfTestScheduled={detailData.data.perf_test_scheduled as boolean}
            perfTestHourOfDay={detailData.data.perf_test_hour_of_day as number}
            perfTestPeerIP={detailData.data.perf_test_peer_ip || ''}
            onSubmit={() => {
              refetch();
              detailRefetch();
            }}
          />
        </DialogStandard>
      )}
      {showGetDiagnosticDialog && selectedItemId && (
        <DialogStandard
          size='md'
          open={showGetDiagnosticDialog}
          handleClose={() => setShowGetDiagnosticDialog(false)}
          title="Get Diagnostics"
        >
          <RouterGetDiagnostic
            cpeId={selectedItemId}
            onSubmit={() => setShowGetDiagnosticDialog(false)}
          />
        </DialogStandard>
      )}
      {showResetLANPortsDialog && selectedItemId && (
        <DialogStandard
          open={showResetLANPortsDialog}
          handleClose={() => setShowResetLANPortsDialog(false)}
          title="Reset LAN Ports"
        >
          <RouterResetLANPort
            cpeId={selectedItemId}
            onSubmit={ () =>{
              setShowResetLANPortsDialog(false);
              refetch();
              detailRefetch();
            }}
          />
        </DialogStandard>
      )}
      {showResetDeviceDialog && (
        <DialogConfirm
          open={showResetDeviceDialog}
          handleClose={() => setShowResetDeviceDialog(false)}
          handleConfirm={handleRouterResetDevice}
          title="Reset CPE?"
          description={`Are you certain you wish to proceed with resetting ${selectedItemId}? Please note that this action is irreversible.`}
        />
      )}
      {showPingDialog && selectedItemId && (
        <DialogStandard
          size='md'
          open={showPingDialog}
          handleClose={() => setShowPingDialog(false)}
          title="Ping & Traceroute"
        >
          <RouterPingAndTraceroute
            cpeId={selectedItemId}
          />
        </DialogStandard>
      )}
      {showConfirmDelete && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handleRouterDelete}
          title="Delete CPE router?"
          description={`Are you certain you wish to proceed with deleting ${selectedItemId}? Please note that this action is irreversible and will result in the permanent removal of CPE router.`}
          confirmationPhrase={selectedItemId}
        />
      )}
      {showHanldeServiceDialog.value && selectedItemId && detailData && (
        <DialogStandard
          open={showHanldeServiceDialog.value}
          handleClose={() => setShowHandleServiceDialog({ ...showHanldeServiceDialog, value: false })}
          title={showHanldeServiceDialog.status ? 'Activate Service' : 'Stop Service'}
        >
          <RouterHandleService
            cpeId={selectedItemId}
            adminState={detailData?.data?.admin_state}
            status={showHanldeServiceDialog.status}
            close={() => {
              setShowHandleServiceDialog({ ...showHanldeServiceDialog, value: false });
              refetch();
              detailRefetch();
            }}
          />
        </DialogStandard>
      )}
      {showAssignEntity && selectedItemId && detailData && (
        <DialogStandard
          open={showAssignEntity}
          handleClose={() => setShowAssignEntity(false)}
          title="Assign Entity"
        >
          <RouterAssignEntity
            onSubmit={() => {
              setShowAssignEntity(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            entityName={detailData?.data?.entity_name}
          />
        </DialogStandard>
      )}
      {showEditSiteDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditSiteDialog}
          handleClose={() => setShowEditSiteDialog(false)}
          title="Edit Site info"
        >
          <RouterSiteEdit
            onSubmit={() => {
              setShowEditSiteDialog(false);
              refetch();
              detailRefetch();
            }}
            cpeId={selectedItemId}
            cpeName={detailData?.data?.cpe_name}
            siteAddress={detailData?.data?.site_address || ''}
            providerServiceID={detailData?.data?.provider_service_id || ''}
            clientCircuitID={detailData?.data?.client_circuit_id || ''}
            pingDnsIp={detailData?.data?.ping_dns_ip || ''}
            pingEmailNotification={detailData?.data?.ping_email_notification || ''}
            pingWebhookNotification={detailData?.data?.ping_webhook_notification || ''}
            defaultGatewayIP={detailData?.data?.ping_gw_ip || ''}
          />
        </DialogStandard>
      )}
      {loadingModalOpen && <PageLoading content="Working on it..." />}
    </PageContent>
  );
};
